import api from './api';
import { Entity, EntityCreate, EntityUpdate } from '../types';

// Helper function to check if it's a network error
const isNetworkError = (error: any): boolean => {
  return !error.response && (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error') || error.message?.includes('ERR_NETWORK'));
};

export const entityApi = {
  // Get all entities
  getEntities: async (): Promise<Entity[]> => {
    try {


      const response = await api.get('/entities');


      // Backend returns entities directly as an array in response.data
      let entities = response.data;




      return entities;
    } catch (error: any) {



      if (isNetworkError(error)) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw new Error(error.response?.data?.detail || 'Failed to fetch entities');
    }
  },

  // Get entity by ID
  getEntity: async (entityId: string): Promise<Entity> => {
    try {
      const response = await api.get(`/entity/${entityId}`);


      // Backend returns entity directly in response.data
      let entity = response.data;

      return entity;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Entity not found');
      }
      if (isNetworkError(error)) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw new Error(error.response?.data?.detail || 'Failed to fetch entity');
    }
  },

  // Create entity
  createEntity: async (entityData: EntityCreate): Promise<Entity> => {
    try {
      const response = await api.post('/entities', entityData);


      // Backend returns entity directly in response.data
      let entity = response.data;

      return entity;
    } catch (error: any) {
      if (error.response?.status === 422) {
        const detail = error.response.data?.detail;
        if (Array.isArray(detail)) {
          throw new Error(detail.map((err: any) => err.msg).join(', '));
        }
        throw new Error('Invalid entity data');
      }
      if (isNetworkError(error)) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw new Error(error.response?.data?.detail || 'Failed to create entity');
    }
  },

  // Update entity
  updateEntity: async (entityId: string, entityData: EntityUpdate): Promise<Entity> => {
    try {
      const response = await api.put(`/entity/${entityId}`, entityData);


      // Backend returns entity directly in response.data
      let entity = response.data;

      return entity;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Entity not found');
      }
      if (error.response?.status === 422) {
        const detail = error.response.data?.detail;
        if (Array.isArray(detail)) {
          throw new Error(detail.map((err: any) => err.msg).join(', '));
        }
        throw new Error('Invalid entity data');
      }
      if (isNetworkError(error)) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw new Error(error.response?.data?.detail || 'Failed to update entity');
    }
  },

  // Process entity
  processEntity: async (entityId: string): Promise<Entity> => {
    try {
      // First get the entity to get its data
      const entityResponse = await api.get(`/entity/${entityId}`);


      // Try different possible data fields - use nullish coalescing for better checks
      const entityData = entityResponse.data.data ?? entityResponse.data.content ?? entityResponse.data.text ?? entityResponse.data.body ?? "Default processing data";


      // Send the correct request body format: { data: "actual data" }
      const requestBody = { data: entityData };


      const response = await api.post(`/entity/${entityId}/process`, requestBody);


      // Handle different response structures
      let processedEntity = response.data;
      if (response.data && response.data.data) {
        processedEntity = response.data.data; // Backend returns { data: {...} }
      }


      return processedEntity;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Entity not found');
      }
      if (error.response?.status === 400) {
        throw new Error('Entity is already processed or cannot be processed');
      }
      if (error.response?.status === 422) {
        throw new Error('Invalid request format. Please check the data being sent.');
      }
      if (isNetworkError(error)) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw new Error(error.response?.data?.detail || 'Failed to process entity');
    }
  },

  // Upload entity file
  uploadEntity: async (file: File, description?: string): Promise<Entity> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (description) {
        formData.append('description', description);
      }

      const response = await api.post('/entities/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error: any) {
      if (isNetworkError(error)) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw new Error(error.response?.data?.detail || 'Failed to upload entity file');
    }
  },

  // Delete entity
  deleteEntity: async (entityId: string): Promise<void> => {
    try {
      await api.delete(`/entity/${entityId}`);
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Entity not found');
      }
      throw new Error(error.response?.data?.detail || 'Failed to delete entity');
    }
  },
};
